/**
 * client/src/pages/Status.tsx — `GET /api/v1/status`. Karti's window into spark-1 being down.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE PAGE IS MOSTLY RED AND THAT IS THE STEADY STATE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `StatusReport.tts` is documented in `shared/protocol.ts` as "null when unreachable. **Unreachable
 * is not an error here; it is the expected steady state.**" spark-1 has no systemd unit *on purpose*
 * and is recovered by a human typing `kuda scene activate 115`.
 *
 * That is why the dot has three states and not two. "Asleep" and "down" send Karti to two different
 * places — one is a command he types, the other is a box he has to go and look at — and a page that
 * painted both red would have thrown away the only distinction it exists to make. The mapping, and
 * it is the whole design of this file:
 *
 *   · **asleep** — we reached something and it told us it is not ready: `/health` answered with
 *     `modelLoaded: false`. One `kuda scene activate 115` away.
 *   · **down** — we reached nothing at all: `tts` is null, or a dependency reports `reachable:
 *     false`. That is a network, a unit, or a box.
 *   · **up** — reachable and loaded.
 *
 * ⚠️ **VOX-LAW 28: `/health` is liveness, local only.** This page reads `/api/v1/status`, which is
 * the *dependency report*. They are different routes with different audiences and conflating them is
 * how a liveness probe ends up doing four network round trips per second on a shared GPU box.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ON THE STRINGS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * This is the console, which `CONSOLE` in `shared/voice/copy.ts` exempts from §3.2's vocabulary
 * rule — the internal names are good and they are the right words here.
 *
 * They still come from `copy.ts`, and **`CONSOLE.status` is the group that used to be missing.** The
 * labels below were bare literals with a note saying they would move "when a `CONSOLE.status` group
 * lands"; it has landed, and they moved. The reason the rule applies even on Karti's own screens is
 * the one `copy.ts` gives about itself: *"a second place for strings is how the first place stops
 * being the only place."*
 *
 * One of them earns its place as a whole sentence rather than a label: `CONSOLE.status.ttsAsleep`
 * carries the recovery command verbatim. A status page whose answer to "spark-1 is unreachable" is
 * "go and look" has failed at the only thing it exists for.
 */

import { useCallback, useEffect, useState } from "react";
import type { StatusReport } from "../../../shared/protocol.ts";
import { JOB_LANES } from "../../../shared/config.ts";
import { CONSOLE } from "../../../shared/voice/copy.ts";
import { api, describeForConsole } from "../lib/api.ts";
import { Button, ConsoleFrame, StatusDot } from "../ui/primitives.tsx";

/**
 * How often to re-ask. 15 s.
 *
 * The report costs one `/health` round trip to spark-1, one to the ASR, one to the origin on
 * amd-server, plus a queue count and two `statfs` calls. Polling it at a UI-ish 2 s would put four
 * requests per second onto the box whose watchdog stops Chatterbox when `MemAvailable` falls — i.e.
 * the page that watches for the outage would be contributing to it. 15 s is fast enough that a
 * `kuda scene activate 115` shows up before the person who typed it has switched windows back.
 */
const POLL_MS = 15_000;

type Dot = "up" | "asleep" | "down";

export function Status() {
  const [report, setReport] = useState<StatusReport | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [at, setAt] = useState<number | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setReport(await api.status(signal));
      setFailed(null);
      setAt(Date.now());
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      // On the console a failure is shown in full. This page's own unreachability is a fact about
      // cloud-2 and is the single most useful thing it can report when it cannot report anything.
      const d = describeForConsole(err);
      setFailed(`${d.where}: ${d.message}`);
      setAt(Date.now());
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    const id = setInterval(() => void load(ac.signal), POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const tts: Dot = !report?.tts ? "down" : report.tts.modelLoaded ? "up" : "asleep";

  return (
    <ConsoleFrame>
      <header>
        <p className="vox-eyebrow">
          <StatusDot state={failed ? "down" : report?.ok ? "up" : "asleep"} />
          {report ? report.env : failed ? CONSOLE.status.unreachable : "…"}
        </p>
        <h1>{CONSOLE.status.title}</h1>
        {at && <p className="vox-quiet">read at {new Date(at).toISOString().slice(11, 19)}Z</p>}
        <div className="vox-btn-row" style={{ marginTop: "var(--space-3)" }}>
          <Button onClick={() => void load()}>{CONSOLE.status.reread}</Button>
        </div>
      </header>

      {/* This page's own unreachability is a fact about cloud-2 and is the single most useful thing
          it can report when it cannot report anything else. The sentence says which box. */}
      {failed && (
        <section>
          <p className="vox-quiet">{CONSOLE.status.failed}</p>
          <pre className="vox-log vox-notice-hard">{failed}</pre>
        </section>
      )}

      {report && (
        <>
          {/*
            §12.4 / `shared/config.ts`: warnings are "degraded-but-running states. Printed at boot,
            never silent." They are first on this page because a warning is a thing somebody chose
            and then forgot, and the bottom of a page is where those go to be forgotten again.
          */}
          {report.warnings.length > 0 && (
            <section>
              <h2>{CONSOLE.status.warnings}</h2>
              <pre className="vox-log">{report.warnings.join("\n")}</pre>
            </section>
          )}

          <section>
            <h2>{CONSOLE.status.dependencies}</h2>
            <dl className="vox-facts">
              <dt>
                <StatusDot state={tts} />
                {CONSOLE.status.tts}
              </dt>
              <dd className="vox-mono">
                {report.tts
                  ? `modelLoaded=${String(report.tts.modelLoaded)} dtype=${report.tts.dtype ?? "null"} rtt=${
                      report.tts.rttMs
                    }ms variants=[${report.tts.variants.join(", ")}]`
                  : CONSOLE.status.ttsAsleep}
              </dd>

              <dt>
                <StatusDot state={report.asr.reachable ? "up" : "down"} />
                {CONSOLE.status.asr}
              </dt>
              <dd className="vox-mono">
                {report.asr.reachable ? `rtt=${report.asr.rttMs ?? "?"}ms` : CONSOLE.status.unreachable}
              </dd>

              <dt>
                <StatusDot state={report.origin.reachable ? "up" : "down"} />
                {CONSOLE.status.origin}
              </dt>
              <dd className="vox-mono">
                {report.origin.reachable ? `rtt=${report.origin.rttMs ?? "?"}ms` : CONSOLE.status.unreachable}
              </dd>
            </dl>
          </section>

          <section>
            <h2>{CONSOLE.status.queue}</h2>
            {/*
              The three lanes come from `JOB_LANES` in `shared/config.ts` rather than from the keys
              of `report.queue`, so a lane the server has stopped reporting shows as 0/0 instead of
              silently vanishing from the page. A missing row is the one queue failure that looks
              exactly like an idle queue.
            */}
            <dl className="vox-facts">
              {JOB_LANES.map((lane) => {
                const cell = report.queue[lane];
                return (
                  <div key={lane} style={{ display: "contents" }}>
                    <dt>{lane}</dt>
                    <dd className="vox-mono">
                      {cell ? `${cell.running} running · ${cell.queued} queued` : CONSOLE.status.queueIdle}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>

          <section>
            <h2>{CONSOLE.status.disk}</h2>
            <dl className="vox-facts">
              <dt>{CONSOLE.status.diskApp}</dt>
              <dd className="vox-mono">{gib(report.disk.dataDirFreeBytes)} free</dd>
              <dt>{CONSOLE.status.diskStorage}</dt>
              {/*
                Null is not zero and this page must say which. §12.2: cloud-2 has 56 G at 72% and
                amd-server has 852 G on `/mnt/storage`; "we did not ask" and "the disk is full" send
                Karti to opposite conclusions, and `optNum` in `lib/api.ts` is what keeps them apart
                on the wire.
              */}
              <dd className="vox-mono">
                {report.disk.storageDirFreeBytes === null
                  ? CONSOLE.status.notReported
                  : `${gib(report.disk.storageDirFreeBytes)} free`}
              </dd>
            </dl>
          </section>

          {/*
            ⚠️ **The two silent gaps, and they are on this page because nothing else can show them.**
            Every other section is a machine answering or not answering. These two are states where
            the whole chain is green and the product still under-delivers with nothing in any log:
            a finished clone that never spoke because §3.13's sentence was never authored, and a
            studio that silently became "read this" instead of "repeat after me" because the prompt
            recordings are 404s on a static path (§3.7, deviation 1's reversal).
          */}
          <section>
            <h2>{CONSOLE.status.payoff}</h2>
            <dl className="vox-facts">
              <dt>{CONSOLE.status.payoffWaiting}</dt>
              <dd className="vox-mono">
                {report.payoff.sessionsWaitingOnASentence} ({report.payoff.sessionsRevealed} revealed)
              </dd>
              <dt>{CONSOLE.status.promptAudio}</dt>
              <dd className="vox-mono">
                {report.payoff.promptAudio.recorded} / {report.payoff.promptAudio.expected}
                {report.payoff.promptAudio.complete ? "" : ` — ${CONSOLE.status.promptAudioMissing}`}
              </dd>
            </dl>
          </section>

          <section>
            <h2>{CONSOLE.status.raw}</h2>
            <pre className="vox-log">{JSON.stringify(report, null, 2)}</pre>
          </section>
        </>
      )}
    </ConsoleFrame>
  );
}

/** GiB to one decimal. Binary, because that is what `df` and every runbook number in §12.2 are. */
function gib(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
}
